import React, { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useLanguage } from '../../../../context/LanguageContext';
import '../style/form-dialog.css';

interface FormDialogProps {
    isOpen: boolean;
    type: 'success' | 'error';
    message?: string;
    onClose: () => void;
}

const FormDialog: React.FC<FormDialogProps> = ({ isOpen, type, message, onClose }) => {
    const { language } = useLanguage();
    const location = useLocation();
    const navigate = useNavigate();
    
    // Check if we're on the home page
    const isHomePage = location.pathname === '/';
    
    // Show "Go to Home Page" button only if:
    // 1. We're not on the home page
    // 2. The dialog type is 'success'
    const showHomeButton = !isHomePage && type === 'success';

    useEffect(() => {
        if (isOpen) {
            const handleEscape = (e: KeyboardEvent) => {
                if (e.key === 'Escape') {
                    onClose();
                }
            };
            document.addEventListener('keydown', handleEscape);
            return () => document.removeEventListener('keydown', handleEscape);
        }
    }, [isOpen, onClose]);

    if (!isOpen) return null;

    const successTitle = language === 'pt' ? 'Formulário Enviado' : 'Form Sent';
    const successMessage = message || (language === 'pt' 
        ? 'O seu formulário foi enviado com sucesso. Entraremos em contacto em breve.'
        : 'Your form was sent successfully. We will contact you soon.');
    
    const errorTitle = language === 'pt' ? 'Ocorreu um erro' : 'Something went wrong';
    const errorMessage = message || (language === 'pt' 
        ? 'Não foi possível enviar o seu pedido. Tente novamente mais tarde.'
        : 'We could not send your request. Please try again later.');
    
    const closeText = language === 'pt' ? 'Fechar' : 'Close';
    const goToHomeText = language === 'pt' ? 'Ir para a Página Inicial' : 'Go to Home Page';
    
    const handleGoToHome = () => {
        onClose();
        navigate('/');
    };

    return (
        <div className="form-dialog__backdrop" onClick={(e) => {
            if (e.target === e.currentTarget) {
                onClose();
            }
        }}>
            <div className="form-dialog__container" role="dialog" aria-modal="true">
                <div className={`form-dialog__icon ${type}`}>
                    {type === 'success' ? '✅' : '⚠️'}
                </div>
                <h4 className="form-dialog__title">
                    {type === 'success' ? successTitle : errorTitle}
                </h4>
                <p className="form-dialog__message">
                    {type === 'success' ? successMessage : errorMessage}
                </p>
                <div className="form-dialog__actions">
                    {showHomeButton && (
                        <button className="form-dialog__btn form-dialog__btn--primary" onClick={handleGoToHome}>
                            {goToHomeText}
                        </button>
                    )}
                    <button className="form-dialog__btn" onClick={onClose}>
                        {closeText}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default FormDialog;

